import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    LogOut,
    Lock,
    ShoppingBag,
    Package,
    MessageSquare,
    Building2,
    IndianRupee,
    Download,
    RefreshCw,
    Search,
    ChevronDown,
    ChevronRight,
    TrendingUp,
    BarChart3,
    Printer,
    Undo2,
    StickyNote,
    Calendar,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ostrich_admin_token";

const STATUS_OPTIONS = [
    "received",
    "payment_pending",
    "payment_failed",
    "payment_cancelled",
    "paid",
    "processing",
    "shipped",
    "delivered",
    "cancelled",
];
const STATUS_COLOR = {
    received: "bg-sun-cream text-sun-ink",
    payment_pending: "bg-orange-100 text-orange-700",
    payment_failed: "bg-red-100 text-red-800",
    payment_cancelled: "bg-gray-200 text-gray-700",
    paid: "bg-emerald-100 text-emerald-800",
    processing: "bg-sun-gold/20 text-sun-ink",
    shipped: "bg-blue-100 text-blue-800",
    delivered: "bg-green-100 text-green-800",
    refunded: "bg-purple-100 text-purple-800",
    cancelled: "bg-red-100 text-red-800",
};

// ─────────────────────────── ANALYTICS ───────────────────────────
const RANGES = [
    { id: "7", label: "7d", days: 7 },
    { id: "30", label: "30d", days: 30 },
    { id: "90", label: "90d", days: 90 },
    { id: "all", label: "All", days: 0 },
];

const AnalyticsPanel = ({ token, onAuthFail, range, onRangeChange }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        setLoading(true);
        (async () => {
            try {
                const res = await fetch(`${API}/admin/analytics?days=${range.days}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (res.status === 401) return onAuthFail();
                if (!res.ok) throw new Error("analytics failed");
                const json = await res.json();
                if (active) setData(json);
            } catch {
                /* ignore */
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => {
            active = false;
        };
    }, [token, range, onAuthFail]);

    const totals = data?.totals || {};
    const daily = data?.daily || [];
    const top = data?.top_products || [];
    const payments = data?.payment_methods || [];

    const maxRevenue = Math.max(1, ...daily.map((d) => d.revenue));
    const totalPayments = payments.reduce((s, p) => s + p.count, 0) || 1;

    return (
        <section data-testid="admin-analytics" className="mb-10">
            {/* Range chips */}
            <div className="flex items-center justify-between gap-3 mb-5">
                <div className="overline">— Performance · last {range.label}</div>
                <div className="flex gap-1 p-1 bg-white border border-sun-line rounded-full">
                    {RANGES.map((r) => (
                        <button
                            key={r.id}
                            onClick={() => onRangeChange(r)}
                            data-testid={`range-${r.id}`}
                            className={`px-4 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
                                range.id === r.id
                                    ? "bg-sun-ink text-white"
                                    : "text-sun-inkSoft hover:text-sun-ink"
                            }`}
                        >
                            {r.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
                <KpiCard label="Revenue" value={loading ? "…" : formatINR(totals.revenue || 0)} accent="bg-sunrise text-white" icon={IndianRupee} />
                <KpiCard label="Paid orders" value={loading ? "…" : totals.paid_orders ?? 0} accent="bg-emerald-100 text-emerald-800" icon={ShoppingBag} />
                <KpiCard label="Avg order value" value={loading ? "…" : formatINR(totals.avg_order_value || 0)} accent="bg-sun-cream text-sun-orange" icon={TrendingUp} />
                <KpiCard label="B2B inquiries" value={loading ? "…" : totals.inquiries ?? 0} accent="bg-sun-ink text-sun-gold" icon={Building2} />
            </div>

            {/* Charts row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* Revenue chart */}
                <div className="lg:col-span-2 bg-white border border-sun-line rounded-3xl p-6">
                    <div className="flex items-center justify-between mb-5">
                        <div>
                            <div className="overline mb-1">— Revenue by day</div>
                            <div className="font-heading text-2xl text-sun-ink">
                                {formatINR(totals.revenue || 0)}
                                <span className="font-mono text-xs uppercase tracking-[0.18em] text-sun-muted ml-3">
                                    {totals.paid_orders || 0} orders
                                </span>
                            </div>
                        </div>
                        <BarChart3 className="w-5 h-5 text-sun-muted" />
                    </div>
                    {daily.length === 0 ? (
                        <div className="h-40 flex items-center justify-center font-mono text-[10px] uppercase tracking-[0.22em] text-sun-muted">
                            No paid orders in this window
                        </div>
                    ) : (
                        <div className="flex items-end gap-1.5 h-44 pt-3" data-testid="revenue-chart">
                            {daily.map((d) => {
                                const h = Math.max(4, (d.revenue / maxRevenue) * 100);
                                const day = new Date(d.date);
                                const dayLabel = day.toLocaleDateString("en-IN", { day: "2-digit" });
                                return (
                                    <div key={d.date} className="flex-1 flex flex-col items-center group relative">
                                        <div
                                            className="w-full bg-gradient-to-t from-sun-orange to-sun-gold rounded-t-md hover:opacity-80 transition-opacity"
                                            style={{ height: `${h}%` }}
                                            title={`${d.date}: ${formatINR(d.revenue)} · ${d.orders} orders`}
                                        />
                                        <div className="font-mono text-[8px] uppercase tracking-[0.16em] text-sun-muted mt-1.5 group-hover:text-sun-ink">
                                            {dayLabel}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Top products */}
                <div className="bg-white border border-sun-line rounded-3xl p-6">
                    <div className="overline mb-4">— Top products</div>
                    {top.length === 0 ? (
                        <div className="h-32 flex items-center justify-center font-mono text-[10px] uppercase tracking-[0.22em] text-sun-muted">
                            No data
                        </div>
                    ) : (
                        <div className="space-y-3" data-testid="top-products">
                            {top.slice(0, 5).map((p, i) => {
                                const max = top[0]?.revenue || 1;
                                const pct = (p.revenue / max) * 100;
                                return (
                                    <div key={p.product_id}>
                                        <div className="flex items-baseline justify-between text-sm mb-1">
                                            <span className="text-sun-ink truncate">
                                                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-sun-muted mr-2">
                                                    {String(i + 1).padStart(2, "0")}
                                                </span>
                                                {p.product_name}
                                            </span>
                                            <span className="font-heading text-sun-ink ml-2 whitespace-nowrap">
                                                {formatINR(p.revenue)}
                                            </span>
                                        </div>
                                        <div className="h-1.5 bg-sun-cream rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-gradient-to-r from-sun-gold to-sun-orange"
                                                style={{ width: `${pct}%` }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Payment methods mini */}
                    {payments.length > 0 && (
                        <div className="mt-6 pt-6 border-t border-sun-line">
                            <div className="overline mb-3">— Payment mix</div>
                            <div className="flex h-2 rounded-full overflow-hidden bg-sun-cream">
                                {payments.map((p, i) => (
                                    <div
                                        key={p.method}
                                        className={i === 0 ? "bg-sun-orange" : i === 1 ? "bg-sun-gold" : "bg-sun-ink"}
                                        style={{ width: `${(p.count / totalPayments) * 100}%` }}
                                        title={`${p.method}: ${p.count}`}
                                    />
                                ))}
                            </div>
                            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                                {payments.map((p, i) => (
                                    <div key={p.method} className="flex items-center gap-1.5">
                                        <span
                                            className={`w-2 h-2 rounded-full ${
                                                i === 0 ? "bg-sun-orange" : i === 1 ? "bg-sun-gold" : "bg-sun-ink"
                                            }`}
                                        />
                                        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-sun-muted">
                                            {p.method}
                                        </span>
                                        <span className="font-medium text-sun-ink">{p.count}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

const KpiCard = ({ label, value, accent, icon: Icon }) => (
    <div className="bg-white border border-sun-line rounded-3xl p-5 flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-sun-muted">
                {label}
            </div>
            <div className="font-heading text-2xl md:text-3xl text-sun-ink mt-1.5 leading-none truncate">
                {value}
            </div>
        </div>
        <div className={`w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 ${accent}`}>
            <Icon className="w-4 h-4" strokeWidth={2} />
        </div>
    </div>
);

const formatDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
};

const formatINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

// ─────────────────────────── LOGIN ───────────────────────────
const LoginCard = ({ onSuccess }) => {
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await fetch(`${API}/admin/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ password }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || "Invalid password");
            }
            const { token } = await res.json();
            localStorage.setItem(TOKEN_KEY, token);
            toast.success("Welcome back");
            onSuccess(token);
        } catch (err) {
            toast.error(err.message || "Login failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-sun-bg flex items-center justify-center px-6">
            <div className="absolute -top-32 -right-32 w-[40rem] h-[40rem] sun-rays opacity-15 animate-spin-slow pointer-events-none" />
            <div className="w-full max-w-md relative">
                <Link
                    to="/"
                    className="font-mono text-[10px] uppercase tracking-[0.28em] text-sun-muted hover:text-sun-orange inline-flex items-center gap-2 mb-8"
                >
                    ← Back to site
                </Link>
                <div className="bg-white border border-sun-line rounded-3xl p-10 shadow-xl shadow-sun-orange/5">
                    <div className="w-14 h-14 rounded-2xl bg-sun-ink text-sun-gold flex items-center justify-center mb-6">
                        <Lock className="w-6 h-6" strokeWidth={2} />
                    </div>
                    <div className="overline mb-2">— Admin</div>
                    <h1 className="font-heading text-4xl text-sun-ink leading-[1] tracking-[-0.02em]">
                        Ostrich <span className="display-italic text-gradient">control room.</span>
                    </h1>
                    <p className="mt-3 text-sun-inkSoft text-sm">
                        Enter the admin password to view orders, B2B inquiries and messages.
                    </p>

                    <form onSubmit={submit} className="mt-8 space-y-4">
                        <input
                            data-testid="admin-password-input"
                            type="password"
                            autoFocus
                            placeholder="Admin password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full px-5 py-4 bg-sun-cream border border-sun-line rounded-2xl font-mono text-sm focus:outline-none focus:border-sun-orange focus:ring-2 focus:ring-sun-orange/20"
                        />
                        <button
                            type="submit"
                            disabled={loading || !password}
                            data-testid="admin-login-submit"
                            className="btn-gradient w-full py-4 disabled:opacity-50"
                        >
                            {loading ? "Verifying…" : "Sign in"}
                        </button>
                    </form>

                    <div className="mt-6 text-[11px] font-mono text-sun-muted leading-relaxed">
                        Owner-only access · Token kept on this device · Sign out wipes it.
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─────────────────────────── STAT CARD ───────────────────────────
const StatCard = ({ icon: Icon, label, value, accent }) => (
    <div className="bg-white border border-sun-line rounded-3xl p-6 flex items-start gap-4">
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${accent}`}>
            <Icon className="w-5 h-5" strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-sun-muted">
                {label}
            </div>
            <div className="font-heading text-3xl text-sun-ink mt-1 leading-none truncate">{value}</div>
        </div>
    </div>
);

// ─────────────────────────── ORDERS TAB ───────────────────────────
const OrdersTab = ({ token, onAuthFail }) => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState(null);
    const [query, setQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API}/orders`);
            if (!res.ok) throw new Error("Failed to load orders");
            const data = await res.json();
            setOrders(data);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const updateStatus = async (id, status) => {
        try {
            const res = await fetch(`${API}/admin/orders/${id}/status`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ status }),
            });
            if (res.status === 401) {
                onAuthFail();
                return;
            }
            if (!res.ok) throw new Error("Update failed");
            const updated = await res.json();
            setOrders((prev) => prev.map((o) => (o.id === id ? updated : o)));
            toast.success(`Status → ${status}`);
        } catch (err) {
            toast.error(err.message);
        }
    };

    const saveNotes = async (id, admin_notes) => {
        try {
            const res = await fetch(`${API}/admin/orders/${id}/notes`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ admin_notes }),
            });
            if (res.status === 401) return onAuthFail();
            if (!res.ok) throw new Error("Save failed");
            const updated = await res.json();
            setOrders((prev) => prev.map((o) => (o.id === id ? updated : o)));
            toast.success("Note saved");
        } catch (err) {
            toast.error(err.message);
        }
    };

    const refundOrder = async (order) => {
        const reason = window.prompt(
            `Refund full amount ₹${order.total ?? order.subtotal} for ${order.order_number}?\n\nEnter a reason (optional, will be visible to Razorpay):`,
            "",
        );
        if (reason === null) return;
        try {
            const res = await fetch(`${API}/admin/orders/${order.id}/refund`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ reason }),
            });
            if (res.status === 401) return onAuthFail();
            const json = await res.json();
            if (!res.ok) throw new Error(json.detail || "Refund failed");
            toast.success(`Refunded ₹${json.refund_amount}`);
            await load();
        } catch (err) {
            toast.error(err.message);
        }
    };

    const printInvoice = (order) => {
        const win = window.open("", "_blank", "width=820,height=900");
        if (!win) return toast.error("Allow popups to print invoice");
        const items = (order.items || []).map((i) => `
            <tr>
                <td style="padding:10px 12px;border-bottom:1px solid #EDE3D6;">
                    ${i.product_name}<br>
                    <span style="font-size:10px;letter-spacing:.16em;text-transform:uppercase;color:#8a96a2;">${i.pack_size}</span>
                </td>
                <td style="padding:10px 12px;border-bottom:1px solid #EDE3D6;text-align:right;font-family:'JetBrains Mono',monospace;">₹${i.unit_price}</td>
                <td style="padding:10px 12px;border-bottom:1px solid #EDE3D6;text-align:right;">×${i.quantity}</td>
                <td style="padding:10px 12px;border-bottom:1px solid #EDE3D6;text-align:right;font-weight:600;">₹${i.unit_price * i.quantity}</td>
            </tr>`).join("");
        const total = order.total ?? Math.max(0, (order.subtotal || 0) - (order.discount || 0));
        const html = `<!doctype html><html><head><meta charset="utf-8"><title>Invoice ${order.order_number}</title>
            <style>
                @media print { @page { margin: 16mm; } body { -webkit-print-color-adjust: exact; } }
                body{font-family:Georgia,serif;color:#0F2A3F;background:#FFF8F1;margin:0;padding:32px;}
                .wrap{max-width:760px;margin:0 auto;background:#fff;border:1px solid #EDE3D6;border-radius:16px;overflow:hidden;}
                .head{padding:24px 28px;background:linear-gradient(135deg,#FFB800,#F4571F);color:#fff;}
                .h1{font-size:26px;letter-spacing:-.01em;margin:0;}
                .sub{font-family:'JetBrains Mono',monospace;font-size:10px;letter-spacing:.24em;text-transform:uppercase;opacity:.9;margin-top:4px;}
                .body{padding:24px 28px;}
                .row{display:flex;justify-content:space-between;gap:24px;margin-bottom:18px;}
                .label{font-family:'JetBrains Mono',monospace;font-size:10px;letter-spacing:.22em;text-transform:uppercase;color:#8a96a2;}
                .v{font-size:14px;line-height:1.5;margin-top:4px;}
                table{width:100%;border-collapse:collapse;margin-top:8px;font-size:14px;}
                th{font-family:'JetBrains Mono',monospace;font-size:10px;letter-spacing:.22em;text-transform:uppercase;color:#8a96a2;text-align:left;padding:10px 12px;border-bottom:2px solid #EDE3D6;}
                th.r{text-align:right;}
                .totals{margin-top:14px;font-size:14px;}
                .totals .ln{display:flex;justify-content:space-between;padding:4px 12px;}
                .totals .grand{font-family:Georgia,serif;font-size:20px;border-top:1px solid #EDE3D6;padding-top:10px;margin-top:6px;color:#0F2A3F;}
                .ft{padding:20px 28px;border-top:1px solid #EDE3D6;font-family:'JetBrains Mono',monospace;font-size:10px;letter-spacing:.18em;text-transform:uppercase;color:#8a96a2;display:flex;justify-content:space-between;flex-wrap:wrap;gap:8px;}
                .btn{display:inline-block;margin-top:20px;background:#0F2A3F;color:#fff;padding:10px 20px;border-radius:999px;font-family:'JetBrains Mono',monospace;font-size:11px;letter-spacing:.2em;text-transform:uppercase;cursor:pointer;border:0;}
                @media print { .btn { display:none; } }
            </style></head><body>
            <div class="wrap">
                <div class="head">
                    <div class="h1">Ostrich Foods</div>
                    <div class="sub">Make your health better · Invoice</div>
                </div>
                <div class="body">
                    <div class="row">
                        <div>
                            <div class="label">Invoice</div>
                            <div class="v" style="font-family:'JetBrains Mono',monospace;font-size:18px;">${order.order_number}</div>
                            <div class="label" style="margin-top:10px;">Date</div>
                            <div class="v" style="font-family:'JetBrains Mono',monospace;">${new Date(order.created_at).toLocaleString("en-IN")}</div>
                            <div class="label" style="margin-top:10px;">FSSAI Lic.</div>
                            <div class="v" style="font-family:'JetBrains Mono',monospace;">10725997001012</div>
                        </div>
                        <div style="text-align:right;">
                            <div class="label">Bill to</div>
                            <div class="v"><b>${order.customer_name}</b></div>
                            <div class="v" style="font-size:12px;color:#395063;">
                                ${order.email}<br>${order.phone}<br>
                                ${order.address_line1}${order.address_line2 ? ", " + order.address_line2 : ""}<br>
                                ${order.city}, ${order.state} ${order.postal_code}<br>${order.country}
                            </div>
                        </div>
                    </div>
                    <table>
                        <thead><tr><th>Item</th><th class="r">Price</th><th class="r">Qty</th><th class="r">Amount</th></tr></thead>
                        <tbody>${items}</tbody>
                    </table>
                    <div class="totals">
                        <div class="ln"><span style="color:#8a96a2;">Subtotal</span><span>₹${order.subtotal}</span></div>
                        ${order.discount > 0 ? `<div class="ln" style="color:#F4571F;"><span>Discount ${order.coupon_code ? "(" + order.coupon_code + ")" : ""}</span><span>− ₹${order.discount}</span></div>` : ""}
                        <div class="ln grand"><b>Total</b><b>₹${total}</b></div>
                        ${order.refunded ? `<div class="ln" style="color:#7c3aed;"><span>Refunded</span><span>− ₹${order.refund_amount}</span></div>` : ""}
                    </div>
                    <button class="btn" onclick="window.print()">Print invoice</button>
                </div>
                <div class="ft">
                    <span>Ostrich Foods · Surat, Gujarat, India · ostrichfoods.in</span>
                    <span>Status: ${order.status || "received"}</span>
                </div>
            </div>
            <script>setTimeout(function(){window.print();},400);</script>
        </body></html>`;
        win.document.open();
        win.document.write(html);
        win.document.close();
    };

    const exportCsv = async () => {
        try {
            const res = await fetch(`${API}/admin/orders.csv`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.status === 401) return onAuthFail();
            if (!res.ok) throw new Error("Export failed");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "ostrich-orders.csv";
            a.click();
            URL.revokeObjectURL(url);
            toast.success("CSV downloaded");
        } catch (err) {
            toast.error(err.message);
        }
    };

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return orders.filter((o) => {
            if (statusFilter !== "all" && o.status !== statusFilter) return false;
            if (!q) return true;
            return (
                o.order_number?.toLowerCase().includes(q) ||
                o.customer_name?.toLowerCase().includes(q) ||
                o.email?.toLowerCase().includes(q) ||
                o.phone?.toLowerCase().includes(q) ||
                o.city?.toLowerCase().includes(q)
            );
        });
    }, [orders, query, statusFilter]);

    return (
        <div data-testid="admin-orders-tab">
            <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
                <div className="relative flex-1">
                    <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-sun-muted" />
                    <input
                        data-testid="orders-search"
                        type="text"
                        placeholder="Search by order #, name, email, phone, city…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        className="w-full pl-11 pr-4 py-3 bg-white border border-sun-line rounded-full text-sm focus:outline-none focus:border-sun-orange"
                    />
                </div>
                <select
                    data-testid="orders-status-filter"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-5 py-3 bg-white border border-sun-line rounded-full font-mono text-xs uppercase tracking-[0.18em] cursor-pointer focus:outline-none focus:border-sun-orange"
                >
                    <option value="all">All statuses</option>
                    {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                            {s}
                        </option>
                    ))}
                </select>
                <button
                    onClick={load}
                    className="px-5 py-3 bg-white border border-sun-line rounded-full font-mono text-xs uppercase tracking-[0.18em] hover:border-sun-orange transition-colors inline-flex items-center gap-2"
                >
                    <RefreshCw className="w-3.5 h-3.5" /> Reload
                </button>
                <button
                    onClick={exportCsv}
                    data-testid="orders-export-csv"
                    className="px-5 py-3 bg-sun-ink text-white rounded-full font-mono text-xs uppercase tracking-[0.18em] hover:bg-sun-orange transition-colors inline-flex items-center gap-2"
                >
                    <Download className="w-3.5 h-3.5" /> CSV
                </button>
            </div>

            <div className="bg-white border border-sun-line rounded-3xl overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-sun-muted font-mono text-xs uppercase tracking-[0.22em]">
                        Loading…
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="p-12 text-center text-sun-muted font-mono text-xs uppercase tracking-[0.22em]">
                        No orders match your filter.
                    </div>
                ) : (
                    <div className="divide-y divide-sun-line">
                        {filtered.map((o) => {
                            const open = expandedId === o.id;
                            return (
                                <div key={o.id} data-testid={`order-row-${o.order_number}`}>
                                    <button
                                        onClick={() => setExpandedId(open ? null : o.id)}
                                        className="w-full p-5 grid grid-cols-12 gap-3 items-center text-left hover:bg-sun-cream/50 transition-colors"
                                    >
                                        <div className="col-span-12 md:col-span-3">
                                            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-sun-muted">
                                                {formatDate(o.created_at)}
                                            </div>
                                            <div className="font-heading text-lg text-sun-ink mt-0.5">
                                                {o.order_number}
                                            </div>
                                        </div>
                                        <div className="col-span-7 md:col-span-3">
                                            <div className="text-sm font-medium text-sun-ink">
                                                {o.customer_name}
                                            </div>
                                            <div className="text-xs text-sun-inkSoft truncate">
                                                {o.email}
                                            </div>
                                        </div>
                                        <div className="col-span-5 md:col-span-2 text-sm text-sun-ink">
                                            {o.city}, {o.state}
                                        </div>
                                        <div className="col-span-6 md:col-span-2 font-heading text-xl text-sun-ink">
                                            {formatINR(o.total ?? o.subtotal)}
                                        </div>
                                        <div className="col-span-6 md:col-span-2 flex items-center justify-end gap-2">
                                            <span
                                                className={`px-3 py-1 rounded-full font-mono text-[10px] uppercase tracking-[0.18em] ${STATUS_COLOR[o.status] || STATUS_COLOR.received}`}
                                            >
                                                {o.status || "received"}
                                            </span>
                                            {open ? (
                                                <ChevronDown className="w-4 h-4 text-sun-muted" />
                                            ) : (
                                                <ChevronRight className="w-4 h-4 text-sun-muted" />
                                            )}
                                        </div>
                                    </button>

                                    {open && (
                                        <div className="px-5 pb-6 pt-2 bg-sun-cream/30">
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                <div className="md:col-span-2">
                                                    <div className="overline mb-3">— Items</div>
                                                    <ul className="space-y-2">
                                                        {(o.items || []).map((it, i) => (
                                                            <li
                                                                key={i}
                                                                className="flex justify-between bg-white border border-sun-line rounded-xl px-4 py-3 text-sm"
                                                            >
                                                                <div>
                                                                    <div className="font-medium text-sun-ink">
                                                                        {it.product_name}
                                                                    </div>
                                                                    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-sun-muted mt-0.5">
                                                                        {it.pack_size} · {formatINR(it.unit_price)} ea
                                                                    </div>
                                                                </div>
                                                                <div className="font-heading text-lg text-sun-ink">
                                                                    × {it.quantity}
                                                                </div>
                                                            </li>
                                                        ))}
                                                    </ul>

                                                    <div className="mt-4 bg-white border border-sun-line rounded-xl p-4 space-y-1.5 text-sm">
                                                        <div className="flex justify-between text-sun-inkSoft">
                                                            <span>Subtotal</span>
                                                            <span>{formatINR(o.subtotal)}</span>
                                                        </div>
                                                        {o.discount > 0 && (
                                                            <div className="flex justify-between text-sun-orange">
                                                                <span>Discount {o.coupon_code ? `(${o.coupon_code})` : ""}</span>
                                                                <span>− {formatINR(o.discount)}</span>
                                                            </div>
                                                        )}
                                                        <div className="flex justify-between font-heading text-lg text-sun-ink pt-1 border-t border-sun-line">
                                                            <span>Total</span>
                                                            <span>{formatINR(o.total ?? o.subtotal)}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="space-y-4">
                                                    <div>
                                                        <div className="overline mb-2">— Customer</div>
                                                        <div className="bg-white border border-sun-line rounded-xl p-4 text-sm space-y-1">
                                                            <div className="font-medium text-sun-ink">
                                                                {o.customer_name}
                                                            </div>
                                                            <a
                                                                href={`mailto:${o.email}`}
                                                                className="block text-sun-orange hover:underline truncate"
                                                            >
                                                                {o.email}
                                                            </a>
                                                            <a
                                                                href={`tel:${o.phone}`}
                                                                className="block text-sun-inkSoft"
                                                            >
                                                                {o.phone}
                                                            </a>
                                                            <div className="text-sun-inkSoft pt-2 border-t border-sun-line mt-2">
                                                                {o.address_line1}
                                                                {o.address_line2 ? `, ${o.address_line2}` : ""}
                                                                <br />
                                                                {o.city}, {o.state} {o.postal_code}
                                                                <br />
                                                                {o.country}
                                                            </div>
                                                            {o.notes && (
                                                                <div className="text-xs italic text-sun-muted pt-2 border-t border-sun-line mt-2">
                                                                    "{o.notes}"
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <div className="overline mb-2">— Update status</div>
                                                        <select
                                                            data-testid={`status-update-${o.order_number}`}
                                                            value={o.status || "received"}
                                                            onChange={(e) => updateStatus(o.id, e.target.value)}
                                                            className="w-full px-4 py-3 bg-white border border-sun-line rounded-xl font-mono text-xs uppercase tracking-[0.18em] cursor-pointer focus:outline-none focus:border-sun-orange"
                                                        >
                                                            {STATUS_OPTIONS.map((s) => (
                                                                <option key={s} value={s}>
                                                                    {s}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    {/* Internal notes */}
                                                    <div>
                                                        <div className="overline mb-2 flex items-center gap-1.5">
                                                            <StickyNote className="w-3 h-3" />
                                                            Internal notes
                                                        </div>
                                                        <OrderNotes
                                                            order={o}
                                                            onSave={(notes) => saveNotes(o.id, notes)}
                                                        />
                                                    </div>

                                                    {/* Quick actions */}
                                                    <div>
                                                        <div className="overline mb-2">— Quick actions</div>
                                                        <div className="space-y-2">
                                                            <button
                                                                onClick={() => printInvoice(o)}
                                                                data-testid={`print-invoice-${o.order_number}`}
                                                                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-sun-ink text-white rounded-xl hover:bg-sun-orange transition-colors font-mono text-[11px] uppercase tracking-[0.2em]"
                                                            >
                                                                <Printer className="w-3.5 h-3.5" />
                                                                Print invoice
                                                            </button>
                                                            {o.razorpay_payment_id && !o.refunded && (
                                                                <button
                                                                    onClick={() => refundOrder(o)}
                                                                    data-testid={`refund-${o.order_number}`}
                                                                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-red-300 text-red-700 rounded-xl hover:bg-red-50 transition-colors font-mono text-[11px] uppercase tracking-[0.2em]"
                                                                >
                                                                    <Undo2 className="w-3.5 h-3.5" />
                                                                    Refund full
                                                                </button>
                                                            )}
                                                            {o.refunded && (
                                                                <div className="text-center px-4 py-2.5 bg-purple-50 border border-purple-200 text-purple-800 rounded-xl font-mono text-[10px] uppercase tracking-[0.2em]">
                                                                    Refunded ₹{o.refund_amount}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

// Inline editable notes block
const OrderNotes = ({ order, onSave }) => {
    const [value, setValue] = useState(order.admin_notes || "");
    const [saving, setSaving] = useState(false);
    const dirty = (order.admin_notes || "") !== value;

    useEffect(() => {
        setValue(order.admin_notes || "");
    }, [order.id, order.admin_notes]);

    const submit = async () => {
        setSaving(true);
        await onSave(value);
        setSaving(false);
    };

    return (
        <div>
            <textarea
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Notes only you can see (e.g., 'gift wrap', 'priority', courier ref)…"
                rows={3}
                data-testid={`admin-notes-${order.order_number}`}
                className="w-full px-3 py-2.5 bg-white border border-sun-line rounded-xl text-sm resize-none focus:outline-none focus:border-sun-orange"
            />
            <button
                onClick={submit}
                disabled={!dirty || saving}
                data-testid={`save-notes-${order.order_number}`}
                className="mt-2 w-full px-3 py-2 rounded-lg bg-sun-cream hover:bg-sun-gold/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-mono text-[10px] uppercase tracking-[0.22em] text-sun-ink"
            >
                {saving ? "Saving…" : dirty ? "Save note" : "Saved"}
            </button>
        </div>
    );
};

// ─────────────────────────── INQUIRIES TAB ───────────────────────────
const InquiriesTab = ({ token, onAuthFail }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API}/inquiries`);
            if (!res.ok) throw new Error("Failed to load");
            setItems(await res.json());
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const exportCsv = async () => {
        try {
            const res = await fetch(`${API}/admin/inquiries.csv`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.status === 401) return onAuthFail();
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "ostrich-inquiries.csv";
            a.click();
            URL.revokeObjectURL(url);
            toast.success("CSV downloaded");
        } catch (err) {
            toast.error(err.message);
        }
    };

    return (
        <div data-testid="admin-inquiries-tab">
            <div className="flex justify-end gap-3 mb-6">
                <button
                    onClick={load}
                    className="px-5 py-3 bg-white border border-sun-line rounded-full font-mono text-xs uppercase tracking-[0.18em] hover:border-sun-orange inline-flex items-center gap-2"
                >
                    <RefreshCw className="w-3.5 h-3.5" /> Reload
                </button>
                <button
                    onClick={exportCsv}
                    data-testid="inquiries-export-csv"
                    className="px-5 py-3 bg-sun-ink text-white rounded-full font-mono text-xs uppercase tracking-[0.18em] hover:bg-sun-orange inline-flex items-center gap-2"
                >
                    <Download className="w-3.5 h-3.5" /> CSV
                </button>
            </div>

            <div className="bg-white border border-sun-line rounded-3xl overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-sun-muted font-mono text-xs uppercase tracking-[0.22em]">
                        Loading…
                    </div>
                ) : items.length === 0 ? (
                    <div className="p-12 text-center text-sun-muted font-mono text-xs uppercase tracking-[0.22em]">
                        No inquiries yet.
                    </div>
                ) : (
                    <div className="divide-y divide-sun-line">
                        {items.map((it) => {
                            const open = expandedId === it.id;
                            return (
                                <div key={it.id}>
                                    <button
                                        onClick={() => setExpandedId(open ? null : it.id)}
                                        className="w-full p-5 grid grid-cols-12 gap-3 items-center text-left hover:bg-sun-cream/50"
                                    >
                                        <div className="col-span-12 md:col-span-3">
                                            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-sun-muted">
                                                {formatDate(it.created_at)}
                                            </div>
                                            <div className="font-heading text-lg text-sun-ink mt-0.5">
                                                {it.company_name}
                                            </div>
                                        </div>
                                        <div className="col-span-12 md:col-span-3 text-sm">
                                            <div className="font-medium text-sun-ink">{it.contact_name}</div>
                                            <div className="text-sun-inkSoft truncate">{it.email}</div>
                                        </div>
                                        <div className="col-span-6 md:col-span-2 text-sm text-sun-ink">
                                            {it.country}
                                        </div>
                                        <div className="col-span-6 md:col-span-3 text-sm text-sun-inkSoft truncate">
                                            {it.estimated_volume}
                                        </div>
                                        <div className="col-span-12 md:col-span-1 text-right">
                                            {open ? (
                                                <ChevronDown className="w-4 h-4 text-sun-muted inline" />
                                            ) : (
                                                <ChevronRight className="w-4 h-4 text-sun-muted inline" />
                                            )}
                                        </div>
                                    </button>
                                    {open && (
                                        <div className="px-5 pb-6 pt-2 bg-sun-cream/30 grid md:grid-cols-2 gap-4 text-sm">
                                            <div className="bg-white border border-sun-line rounded-xl p-4 space-y-1">
                                                <div className="overline mb-2">— Contact</div>
                                                <a
                                                    href={`mailto:${it.email}`}
                                                    className="block text-sun-orange hover:underline"
                                                >
                                                    {it.email}
                                                </a>
                                                <a href={`tel:${it.phone}`} className="block text-sun-inkSoft">
                                                    {it.phone}
                                                </a>
                                            </div>
                                            <div className="bg-white border border-sun-line rounded-xl p-4 space-y-2">
                                                <div className="overline mb-1">— Requirement</div>
                                                <div>
                                                    <span className="text-sun-muted">Products:</span>{" "}
                                                    {it.products_interested}
                                                </div>
                                                <div>
                                                    <span className="text-sun-muted">Volume:</span>{" "}
                                                    {it.estimated_volume}
                                                </div>
                                                {it.application && (
                                                    <div>
                                                        <span className="text-sun-muted">Application:</span>{" "}
                                                        {it.application}
                                                    </div>
                                                )}
                                                {it.message && (
                                                    <div className="pt-2 border-t border-sun-line italic text-sun-inkSoft">
                                                        "{it.message}"
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

// ─────────────────────────── CONTACTS TAB ───────────────────────────
const ContactsTab = ({ token, onAuthFail }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API}/contact`);
            if (!res.ok) throw new Error("Failed to load");
            setItems(await res.json());
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const exportCsv = async () => {
        try {
            const res = await fetch(`${API}/admin/contacts.csv`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.status === 401) return onAuthFail();
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "ostrich-contacts.csv";
            a.click();
            URL.revokeObjectURL(url);
            toast.success("CSV downloaded");
        } catch (err) {
            toast.error(err.message);
        }
    };

    return (
        <div data-testid="admin-contacts-tab">
            <div className="flex justify-end gap-3 mb-6">
                <button
                    onClick={load}
                    className="px-5 py-3 bg-white border border-sun-line rounded-full font-mono text-xs uppercase tracking-[0.18em] hover:border-sun-orange inline-flex items-center gap-2"
                >
                    <RefreshCw className="w-3.5 h-3.5" /> Reload
                </button>
                <button
                    onClick={exportCsv}
                    data-testid="contacts-export-csv"
                    className="px-5 py-3 bg-sun-ink text-white rounded-full font-mono text-xs uppercase tracking-[0.18em] hover:bg-sun-orange inline-flex items-center gap-2"
                >
                    <Download className="w-3.5 h-3.5" /> CSV
                </button>
            </div>

            <div className="bg-white border border-sun-line rounded-3xl overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-sun-muted font-mono text-xs uppercase tracking-[0.22em]">
                        Loading…
                    </div>
                ) : items.length === 0 ? (
                    <div className="p-12 text-center text-sun-muted font-mono text-xs uppercase tracking-[0.22em]">
                        No contact messages yet.
                    </div>
                ) : (
                    <div className="divide-y divide-sun-line">
                        {items.map((it) => {
                            const open = expandedId === it.id;
                            return (
                                <div key={it.id}>
                                    <button
                                        onClick={() => setExpandedId(open ? null : it.id)}
                                        className="w-full p-5 grid grid-cols-12 gap-3 items-center text-left hover:bg-sun-cream/50"
                                    >
                                        <div className="col-span-12 md:col-span-3">
                                            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-sun-muted">
                                                {formatDate(it.created_at)}
                                            </div>
                                            <div className="font-heading text-lg text-sun-ink mt-0.5">
                                                {it.name}
                                            </div>
                                        </div>
                                        <div className="col-span-12 md:col-span-3 text-sm text-sun-inkSoft truncate">
                                            {it.email}
                                        </div>
                                        <div className="col-span-12 md:col-span-5 text-sm text-sun-ink truncate">
                                            {it.subject || it.message?.slice(0, 60)}
                                        </div>
                                        <div className="col-span-12 md:col-span-1 text-right">
                                            {open ? (
                                                <ChevronDown className="w-4 h-4 text-sun-muted inline" />
                                            ) : (
                                                <ChevronRight className="w-4 h-4 text-sun-muted inline" />
                                            )}
                                        </div>
                                    </button>
                                    {open && (
                                        <div className="px-5 pb-6 pt-2 bg-sun-cream/30">
                                            <div className="bg-white border border-sun-line rounded-xl p-4 text-sm space-y-2">
                                                <a
                                                    href={`mailto:${it.email}`}
                                                    className="text-sun-orange hover:underline"
                                                >
                                                    {it.email}
                                                </a>
                                                {it.phone && (
                                                    <a
                                                        href={`tel:${it.phone}`}
                                                        className="block text-sun-inkSoft"
                                                    >
                                                        {it.phone}
                                                    </a>
                                                )}
                                                {it.subject && (
                                                    <div className="font-medium text-sun-ink pt-2 border-t border-sun-line">
                                                        {it.subject}
                                                    </div>
                                                )}
                                                <p className="text-sun-inkSoft leading-relaxed whitespace-pre-wrap">
                                                    {it.message}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

// ─────────────────────────── DASHBOARD ───────────────────────────
const Dashboard = ({ token, onLogout }) => {
    const [tab, setTab] = useState("orders");
    const [range, setRange] = useState(RANGES[1]); // default 30d

    const onAuthFail = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        toast.error("Session expired — please sign in again");
        onLogout();
    }, [onLogout]);

    const tabs = [
        { id: "orders", label: "Orders", icon: ShoppingBag },
        { id: "inquiries", label: "B2B Inquiries", icon: Building2 },
        { id: "contacts", label: "Messages", icon: MessageSquare },
    ];

    return (
        <div className="min-h-screen bg-sun-bg" data-testid="admin-dashboard">
            <header className="bg-white border-b border-sun-line sticky top-0 z-30">
                <div className="container-x py-5 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <Link to="/" className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-white border border-sun-line p-1">
                                <img src="/logo.jpg" alt="Ostrich" className="w-full h-full object-contain" />
                            </div>
                            <div>
                                <div className="font-heading text-xl leading-none text-sun-ink">
                                    Ostrich
                                    <span className="display-italic text-gradient"> admin.</span>
                                </div>
                                <div className="font-mono text-[9px] uppercase tracking-[0.24em] text-sun-muted mt-1">
                                    Control room
                                </div>
                            </div>
                        </Link>
                    </div>
                    <button
                        onClick={onLogout}
                        data-testid="admin-logout"
                        className="px-4 py-2.5 rounded-full bg-sun-cream hover:bg-sun-orange hover:text-white transition-colors font-mono text-[11px] uppercase tracking-[0.2em] inline-flex items-center gap-2"
                    >
                        <LogOut className="w-3.5 h-3.5" /> Sign out
                    </button>
                </div>
            </header>

            <main className="container-x py-10">
                {/* Analytics */}
                <AnalyticsPanel
                    token={token}
                    onAuthFail={onAuthFail}
                    range={range}
                    onRangeChange={setRange}
                />

                {/* Tabs */}
                <div className="flex gap-2 p-1 bg-white border border-sun-line rounded-full max-w-xl mb-8">
                    {tabs.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`tab-${t.id}`}
                            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-full font-mono text-[11px] uppercase tracking-[0.2em] transition-colors ${
                                tab === t.id
                                    ? "bg-sun-ink text-white"
                                    : "text-sun-inkSoft hover:text-sun-ink"
                            }`}
                        >
                            <t.icon className="w-3.5 h-3.5" />
                            {t.label}
                        </button>
                    ))}
                </div>

                {tab === "orders" && <OrdersTab token={token} onAuthFail={onAuthFail} />}
                {tab === "inquiries" && <InquiriesTab token={token} onAuthFail={onAuthFail} />}
                {tab === "contacts" && <ContactsTab token={token} onAuthFail={onAuthFail} />}
            </main>
        </div>
    );
};

// ─────────────────────────── ROOT ───────────────────────────
const AdminDashboard = () => {
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
    const [verified, setVerified] = useState(false);
    const [checking, setChecking] = useState(true);

    useEffect(() => {
        if (!token) {
            setVerified(false);
            setChecking(false);
            return;
        }
        let active = true;
        (async () => {
            try {
                const res = await fetch(`${API}/admin/me`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (active) setVerified(res.ok);
                if (!res.ok) localStorage.removeItem(TOKEN_KEY);
            } catch {
                if (active) setVerified(false);
            } finally {
                if (active) setChecking(false);
            }
        })();
        return () => {
            active = false;
        };
    }, [token]);

    const logout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setVerified(false);
    };

    if (checking) {
        return (
            <div className="min-h-screen bg-sun-bg flex items-center justify-center">
                <div className="font-mono text-xs uppercase tracking-[0.22em] text-sun-muted">
                    Loading…
                </div>
            </div>
        );
    }

    if (!token || !verified) {
        return (
            <LoginCard
                onSuccess={(t) => {
                    setToken(t);
                    setVerified(true);
                }}
            />
        );
    }

    return <Dashboard token={token} onLogout={logout} />;
};

export default AdminDashboard;
